/**
 * Which native SDK versions support which bridge topics.
 *
 * The web app is served to every installed SDK version at once, so a topic
 * introduced today reaches hosts that have no handler for it. Each capability
 * here pairs a topic with the SDK version that first understood it.
 */

/**
 * Returns true if `version` is greater than or equal to `minVersion`.
 * Both must be dot-separated numeric strings (e.g. "1.0.39").
 */
export function semverGte(version: string, minVersion: string): boolean {
  const parts = version.split(".").map(Number);
  const min = minVersion.split(".").map(Number);
  const len = Math.max(parts.length, min.length);
  for (let i = 0; i < len; i++) {
    if ((parts[i] ?? 0) > (min[i] ?? 0)) return true;
    if ((parts[i] ?? 0) < (min[i] ?? 0)) return false;
  }
  return true;
}

export const MIN_SDK_VERSION_FOR_TAB_BAR = "1.0.39";

/** True when the native SDK supports `tabBarVisibility` and coordinated tab bar UX. */
export function sdkSupportsTabBar(sdkVersion: string = "0"): boolean {
  return semverGte(sdkVersion, MIN_SDK_VERSION_FOR_TAB_BAR);
}

/**
 * Set to the SDK version that first ships the local notification handlers.
 * Until iOS and Android pick that release version this stays null, which keeps
 * `sdkSupportsLocalNotifications` false and the capability inert on every host.
 */
export const MIN_SDK_VERSION_FOR_LOCAL_NOTIFICATIONS: string | null = null;

/** True when the native SDK can schedule and cancel on-device notifications. */
export function sdkSupportsLocalNotifications(
  sdkVersion: string = "0"
): boolean {
  if (MIN_SDK_VERSION_FOR_LOCAL_NOTIFICATIONS === null) return false;
  return semverGte(sdkVersion, MIN_SDK_VERSION_FOR_LOCAL_NOTIFICATIONS);
}
